/**
 * KPI karta. Vždy nese n a (kde dává smysl) interval spolehlivosti —
 * hodnota bez vzorku je v tomhle projektu nekompletní informace.
 */
import type { ReactNode } from 'react';
import { formatCi, formatDeltaPercent, formatN } from '@/lib/formatCz';
import { SignificanceBadge } from './SignificanceBadge';
import type { AnyComparison } from '@/lib/significance';
import { RevenueUnavailable } from './NoRevenueData';

export interface MetricCardProps {
  label: string;
  /** předformátovaná hodnota; `null` = nedostupné (NE nula) */
  value: string | null;
  /** vzorek, ze kterého hodnota vznikla */
  n?: number | null;
  ciLow?: number | null;
  ciHigh?: number | null;
  /** relativní změna vs porovnávací období */
  delta?: number | null;
  /** srovnání se statistikou — pak se ukáže i významnost */
  comparison?: AnyComparison | null;
  /** vyšší = lepší (řídí barvu delty) */
  higherIsBetter?: boolean;
  hint?: string;
  footer?: ReactNode;
  /** hodnota chybí kvůli tržbám → vysvětlující pomlčka */
  revenueUnavailable?: boolean;
}

export function MetricCard({
  label,
  value,
  n,
  ciLow,
  ciHigh,
  delta,
  comparison,
  higherIsBetter = true,
  hint,
  footer,
  revenueUnavailable = false,
}: MetricCardProps) {
  const deltaTone =
    delta === null || delta === undefined || delta === 0
      ? 'neutral'
      : (delta > 0) === higherIsBetter
        ? 'good'
        : 'bad';

  return (
    <div className="metric-card">
      <div className="metric-card__label" title={hint}>
        {label}
        {hint ? <span className="metric-card__hint-mark" aria-hidden="true">?</span> : null}
      </div>

      <div className="metric-card__value">
        {revenueUnavailable ? <RevenueUnavailable /> : (value ?? <RevenueUnavailable context="no_data" />)}
      </div>

      {ciLow !== undefined && ciLow !== null && ciHigh !== undefined && ciHigh !== null ? (
        <div className="metric-card__ci">95% CI {formatCi(ciLow, ciHigh)}</div>
      ) : null}

      <div className="metric-card__meta">
        {n !== undefined && n !== null ? (
          <span className="metric-card__n">{formatN(n)}</span>
        ) : null}
        {delta !== undefined && delta !== null ? (
          <span className={`metric-card__delta metric-card__delta--${deltaTone}`}>
            {formatDeltaPercent(delta)}
          </span>
        ) : null}
      </div>

      {comparison ? (
        <div className="metric-card__sig">
          <SignificanceBadge comparison={comparison} compact />
        </div>
      ) : null}

      {footer ? <div className="metric-card__footer">{footer}</div> : null}
    </div>
  );
}

export function MetricCardGrid({ children }: { children: ReactNode }) {
  return <div className="metric-card-grid">{children}</div>;
}
